import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Sun, Moon, Smartphone, Maximize2 } from "lucide-react";
import Lightbox from "./Lightbox";

function ModeIcon({ mode }) {
  if (mode === "light") return <Sun size={12} className="text-rogue-amber" />;
  if (mode === "mobile") return <Smartphone size={12} className="text-rogue-cyan" />;
  return <Moon size={12} className="text-geminii-muted" />;
}

function BrowserFrame({ children, mode, domain = "rogue.geminii-ai.com" }) {
  if (mode === "mobile") {
    return (
      <div className="w-56 mx-auto rounded-[20px] overflow-hidden border-2 border-white/[0.1] shadow-2xl shadow-black/50 bg-black">
        <div className="bg-geminii-card h-5 flex items-center justify-center">
          <div className="w-14 h-1.5 rounded-full bg-white/[0.12]" />
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden border border-white/[0.1] shadow-2xl shadow-black/50">
      <div className="bg-geminii-card flex items-center gap-2 px-4 py-2 border-b border-white/[0.06]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rogue-rose/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-rogue-amber/50" />
          <div className="w-2.5 h-2.5 rounded-full bg-rogue-emerald/50" />
        </div>
        <span className="text-[10px] font-mono text-geminii-dim ml-2">{domain}</span>
        {mode === "light" && (
          <span className="ml-auto text-[9px] font-mono text-rogue-amber/50 flex items-center gap-1">
            <Sun size={9} /> Light
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function GalleryCarousel({ screenshots, domain }) {
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const total = screenshots.length;

  const next = useCallback(() => setCurrent((c) => (c + 1) % total), [total]);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + total) % total), [total]);

  const getIdx = (offset) => (current + offset + total) % total;

  const shot = screenshots[current];
  const prevShot = screenshots[getIdx(-1)];
  const nextShot = screenshots[getIdx(1)];

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  return (
    <div data-testid="gallery-carousel" className="select-none">
      {/* Label */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ModeIcon mode={shot.mode} />
          <span className="text-xs font-medium text-geminii-muted">{shot.label}</span>
        </div>
        <span className="text-[11px] font-mono text-geminii-dim">{current + 1} / {total}</span>
      </div>

      {/* 3-panel gallery */}
      <div className="relative flex items-center justify-center gap-0" style={{ perspective: "1200px" }}>
        {/* Left peek */}
        <button
          onClick={prev}
          data-testid="gallery-prev"
          className="relative z-10 w-[18%] flex-shrink-0 opacity-40 hover:opacity-60 transition-all duration-300 cursor-pointer hidden md:block"
          style={{ transform: "scale(0.85) translateX(20%)", transformOrigin: "right center" }}
          aria-label="Previous screenshot"
        >
          <BrowserFrame mode={prevShot.mode} domain={domain}>
            <img src={prevShot.src} alt={prevShot.label} className="w-full" />
          </BrowserFrame>
        </button>

        {/* Center (active) — clickable to open lightbox */}
        <div className="relative z-20 w-full md:w-[60%] flex-shrink-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative group cursor-zoom-in"
              onClick={openLightbox}
              data-testid="gallery-current"
            >
              <BrowserFrame mode={shot.mode} domain={domain}>
                <img
                  src={shot.src}
                  alt={shot.label}
                  className="w-full"
                  onError={(e) => {
                    // Graceful fallback — if a /light/ asset isn't ready yet, swap to the dark sibling
                    const t = e.currentTarget;
                    if (!t.dataset.fallback && t.src.includes("/light/")) {
                      t.dataset.fallback = "1";
                      t.src = t.src.replace("/light/", "/");
                    }
                  }}
                />
              </BrowserFrame>
              {/* Hover overlay */}
              <div className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center pointer-events-none">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-medium">
                  <Maximize2 size={14} />
                  Click to expand
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Right peek */}
        <button
          onClick={next}
          data-testid="gallery-next"
          className="relative z-10 w-[18%] flex-shrink-0 opacity-40 hover:opacity-60 transition-all duration-300 cursor-pointer hidden md:block"
          style={{ transform: "scale(0.85) translateX(-20%)", transformOrigin: "left center" }}
          aria-label="Next screenshot"
        >
          <BrowserFrame mode={nextShot.mode} domain={domain}>
            <img src={nextShot.src} alt={nextShot.label} className="w-full" />
          </BrowserFrame>
        </button>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-3 mt-6">
        <button
          onClick={prev}
          className="p-2 rounded-lg border border-white/[0.08] text-geminii-dim hover:text-white hover:border-rogue-cyan/30 transition-all"
          aria-label="Previous"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-1.5">
          {screenshots.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === current ? "bg-rogue-cyan w-5" : "bg-white/[0.12] w-1.5 hover:bg-white/[0.25]"
              }`}
            />
          ))}
        </div>
        <button
          onClick={next}
          className="p-2 rounded-lg border border-white/[0.08] text-geminii-dim hover:text-white hover:border-rogue-cyan/30 transition-all"
          aria-label="Next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <Lightbox
        open={lightboxOpen}
        items={screenshots}
        index={current}
        onClose={closeLightbox}
        onPrev={prev}
        onNext={next}
      />
    </div>
  );
}
